/**
 * @fileoverview Mobile Device Detection Hook
 * 
 * Provides a React hook to detect mobile devices based on viewport width.
 * Uses window.matchMedia for responsive breakpoint detection.
 * 
 * @module lib/useMobile
 */

import * as React from 'react';

/**
 * Mobile breakpoint width in pixels
 * Devices with width below this are considered mobile
 * 
 * @constant {number}
 */
const MOBILE_BREAKPOINT = 768;

/**
 * React hook to detect if the current viewport is mobile-sized
 * 
 * Listens to viewport width changes and returns true if width is below
 * the mobile breakpoint (768px by default). Updates automatically on resize.
 * 
 * @returns {boolean} True if viewport width is less than 768px, false otherwise
 * 
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const isMobile = useIsMobile();
 * 
 *   return (
 *     <div>
 *       {isMobile ? <MobileView /> : <DesktopView />}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @remarks
 * - Uses window.matchMedia for efficient media query matching
 * - Automatically cleans up event listeners on unmount
 * - Returns undefined initially until first measurement
 * - Syncs with CSS media queries
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return !!isMobile;
}

export { useIsMobile };
